import type { IContextualMenuItem, IIconProps } from '@fluentui/react';
import { IPIconButton, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { ISingleTag, SingleTag } from 'components/Tags/SingleTag';
import { getEntityName } from 'globalFunctions';
import {
  deleteIcon,
  documentIcon,
  extLinkIcon,
  globalStackTokensGapSmall,
  infoIcon,
  listTypeSitePageLibraryIcon,
  moreIcon,
  pinIcon,
  unPinIcon,
  uploadIcon,
  webIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import {
  createCopilotLibraryDocumentDragPayload,
  setCopilotLibraryDocumentDragPayload,
} from 'models/copilot/copilotLibraryDocumentDragDrop';
import Entity, { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HrefLink from './HrefLink';
import { LinkPreviewModalOrUrl } from './Preview/LinkPreviewModalOrUrl';

// What a host list adds to the row of one link, such as the certificate a supplier's document holds: an icon in place
// of the document icon, a status line under the name (a compact tag and a short text) and the row menu's Details and
// Replace. The row menu takes the place of the delete icon.
export interface IResourceLinkDecoration {
  iconProps: IIconProps;
  tag: ISingleTag;
  text: string;
  onDetails: () => void;
  // undefined when the person may not replace the document; the row's readonly hides it as well
  onReplace?: () => void;
}

// The decoration of one link; undefined for a plain row
export type ResourceLinkDecorationLookup = (link: ResourceLink) => IResourceLinkDecoration | undefined;

interface ISingleResourceLinkProps {
  type?: ResourceListType;
  link: ResourceLink;
  navigateExternal?: boolean;
  readonly: boolean;
  decoration?: IResourceLinkDecoration;
  onDelete: (item: ResourceLink) => void;
  onPin?: (item: ResourceLink, pinned: boolean) => void;
}

const getIcon = (type: ResourceListType, navigateExternal: boolean): JSX.Element => {
  if (navigateExternal) {
    return <IPIconButton iconProps={extLinkIcon} />;
  } else {
    if (type === ResourceListType.CustomList) return <IPIconButton iconProps={documentIcon} />;
    if (type === ResourceListType.DocumentLibrary) return <IPIconButton iconProps={documentIcon} />;
    if (type === ResourceListType.WebURL) return <IPIconButton iconProps={webIcon} />;
    if (type === ResourceListType.SitePageLibrary) return <IPIconButton iconProps={listTypeSitePageLibraryIcon} />;

    return <IPIconButton iconProps={webIcon} />;
  }
};

const SingleResourceLink = (props: ISingleResourceLinkProps) => {
  const { t } = useTranslation(['translation', 'library']);
  const [deleteLink, setDeleteLink] = useState<ResourceLink | undefined>(undefined);
  const [showLink, setShowLink] = useState<ResourceLink | undefined>(undefined);

  const getEntityTypeAndCode = (item: Entity): string => {
    const entityTypeName = getEntityName(item, t as TFunction<string[]>);

    return entityTypeName + ' ' + item.entityName;
  };

  const dragPayload = createCopilotLibraryDocumentDragPayload(props.link);

  // the row menu of a decorated row: Details for every reader, Replace and Delete with the update right
  const getMenuItems = (decoration: IResourceLinkDecoration): IContextualMenuItem[] => {
    const items: IContextualMenuItem[] = [
      {
        key: 'details',
        text: t('library:LinkComponents.Details'),
        iconProps: infoIcon,
        onClick: () => decoration.onDetails(),
      },
    ];
    if (props.readonly) return items;

    if (decoration.onReplace) {
      const onReplace = decoration.onReplace;
      items.push({
        key: 'replace',
        text: t('library:LinkComponents.Replace'),
        iconProps: uploadIcon,
        onClick: () => onReplace(),
      });
    }
    items.push({
      key: 'delete',
      text: t('translation:General.Button.Remove'),
      iconProps: deleteIcon,
      onClick: () => setDeleteLink(props.link),
    });

    return items;
  };

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={globalStackTokensGapSmall}
      verticalAlign="center"
      draggable={!!dragPayload}
      onDragStart={(event) => {
        if (!dragPayload) return;
        setCopilotLibraryDocumentDragPayload(event.dataTransfer, dragPayload);
        event.dataTransfer.effectAllowed = 'copy';
      }}
    >
      {props.type !== undefined && (
        <IPStack.Item onClick={() => setShowLink(props.link)}>
          {props.decoration ? (
            <IPIconButton iconProps={props.decoration.iconProps} />
          ) : (
            getIcon(props.type, props.navigateExternal ?? false)
          )}
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <IPStack horizontal verticalAlign="center">
          <IPStack.Item grow>
            <HrefLink href={props.link.linkURL} newTab onClick={() => setShowLink(props.link)}>
              {props.link.linkName}
            </HrefLink>
            {props.decoration && (
              <IPStack horizontal verticalAlign="center" wrap tokens={globalStackTokensGapSmall}>
                <SingleTag tag={props.decoration.tag} compact />
                <IPText variant="small">{props.decoration.text}</IPText>
              </IPStack>
            )}
          </IPStack.Item>
          <IPStack.Item>
            <IPStack horizontal>
              {props.onPin && !props.link.entity && !props.readonly && (
                //when entity is set, the link in indirect from another entity or up/down in the hierarchy so we cannot delete it
                <IPTooltipHost
                  content={props.link.pinned ? t('library:LinkComponents.UnPin') : t('library:LinkComponents.Pin')}
                >
                  <IPIconButton
                    className="isoplanner-library-singleresource-pin"
                    iconProps={props.link.pinned ? unPinIcon : pinIcon}
                    onClick={() => {
                      if (props.onPin) props.onPin(props.link, !props.link.pinned);
                    }}
                  />
                </IPTooltipHost>
              )}
              {!props.link.entity && props.decoration && (
                <IPIconButton
                  iconProps={moreIcon}
                  menuProps={{ items: getMenuItems(props.decoration), directionalHintFixed: true }}
                  onRenderMenuIcon={() => null}
                />
              )}
              {!props.link.entity && !props.readonly && !props.decoration && (
                //when entity is set, the link in indirect from another entity or up/down in the hierarchy so we cannot delete it
                <IPTooltipHost content={t('translation:General.Button.Remove')}>
                  <IPIconButton
                    iconProps={deleteIcon}
                    onClick={() => {
                      setDeleteLink(props.link);
                    }}
                  />
                </IPTooltipHost>
              )}
              {props.link.entity && (
                <IPTooltipHost
                  calloutProps={{ isBeakVisible: false }}
                  content={
                    <HrefLink
                      entity={props.link.entity}
                      external
                      includeContext
                      disabled={props.link.entity.typeOfEntity === EntityTypes.NotSet}
                    >
                      {getEntityTypeAndCode(props.link.entity)}
                    </HrefLink>
                  }
                >
                  <IPIconButton iconProps={infoIcon} />
                </IPTooltipHost>
              )}
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <DialogYesNo
        hidden={deleteLink === undefined}
        title={t('library:LinkComponents.DeleteTitle')}
        subText={t('library:LinkComponents.DeleteSubText')}
        onYes={() => {
          if (deleteLink && props.onDelete) props.onDelete(deleteLink);
          setDeleteLink(undefined);
        }}
        onNo={() => {
          setDeleteLink(undefined);
        }}
      />
      <LinkPreviewModalOrUrl
        isOpen={showLink !== undefined}
        onClose={() => setShowLink(undefined)}
        link={showLink}
        confirmOpen={false}
        enableLinks={true}
      />
    </IPStack>
  );
};

export default SingleResourceLink;
